/**
 * Chaves de API do cliente — o que destrava a escolha de modelo.
 *
 * A Sophia roda na conta do próprio cliente. Isso muda a ordem da tela: a
 * credencial não é um detalhe de configuração no rodapé, é o primeiro passo.
 * Sem chave não há modelo para escolher, e a tela precisa dizer isso antes de
 * mostrar uma lista vazia que ninguém entende.
 *
 * Três decisões de interface que não são estética:
 *
 * 1. **O campo nunca mostra o que já está guardado.** Volta do servidor só a
 *    máscara (`sk-proj···9f2a`), que serve para reconhecer, não para usar. Um
 *    campo pré-preenchido com a chave seria a credencial trafegando de novo a
 *    cada carregamento da página.
 * 2. **Salvar valida no provedor antes de gravar.** Por isso o botão demora
 *    alguns segundos e diz "Verificando…". Aceitar na hora e falhar depois,
 *    em todo turno da Sophia, é o pior dos mundos: o erro aparece longe daqui.
 * 3. **Remover avisa o que quebra.** Tirar a chave para a Sophia no que
 *    dependia dela — é o comportamento certo, mas tem de ser dito antes.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, IconButton, Paper, Stack, TextField, Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";

export type LinhaChave = {
  provedor: string; dona: string; configurada: boolean;
  mascara: string; por: string; em: string; validada_em: string;
};
export type Chaves = { provedores: LinhaChave[]; tem_alguma: boolean };

const COR_DONA: Record<string, string> = {
  openai: "#10a37f",
  anthropic: "#d97757",
  deepseek: "#4d6bfe",
  openrouter: "#8b5cf6",
  vercel: "#111827",
};

/** Onde o cliente vai buscar a chave. Sem isto, a primeira dúvida vira chamado. */
const ONDE_PEGAR: Record<string, string> = {
  openai: "platform.openai.com/api-keys",
  anthropic: "console.anthropic.com/settings/keys",
  deepseek: "platform.deepseek.com/api_keys",
  openrouter: "openrouter.ai/keys",
  vercel: "vercel.com/dashboard/ai-gateway",
};

function quando(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR");
}

function UmaChave({ linha }: { linha: LinhaChave }) {
  const api = useApi();
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const [valor, setValor] = useState("");
  const [erro, setErro] = useState("");

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["settings-llm", tenant] });
  };

  const gravar = useMutation({
    mutationFn: () =>
      api.call(`/api/settings/llm/chaves/${linha.provedor}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chave: valor.trim() }),
      }),
    onSuccess: () => { setValor(""); setErro(""); recarregar(); },
    onError: (e: unknown) => {
      const d = (e as { detalhes?: string[] })?.detalhes;
      setErro(d?.length ? d.join("; ") : (e as Error)?.message || "Não consegui salvar.");
    },
  });

  const remover = useMutation({
    mutationFn: () =>
      api.call(`/api/settings/llm/chaves/${linha.provedor}`, { method: "DELETE" }),
    onSuccess: () => { setErro(""); recarregar(); },
  });

  const cor = COR_DONA[linha.provedor] ?? "#64748b";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, borderRadius: 2,
        borderColor: linha.configurada ? "success.light" : "divider",
        bgcolor: linha.configurada ? "success.50" : "background.paper",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: cor }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{linha.dona}</Typography>
        {linha.configurada && (
          <>
            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: "success.main" }} />
            <Chip
              size="small" label={linha.mascara}
              sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip arrow title="Remover. A Sophia para de usar os modelos desta empresa.">
              <IconButton
                size="small" disabled={remover.isPending}
                onClick={() => remover.mutate()}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      {linha.configurada ? (
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
          Cadastrada por {linha.por || "—"}
          {quando(linha.em) && ` em ${quando(linha.em)}`}
          {linha.validada_em && " · verificada no provedor"}
        </Typography>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField
            size="small" type="password" fullWidth
            placeholder={`Chave de API da ${linha.dona}`}
            value={valor}
            onChange={(e) => { setValor(e.target.value); setErro(""); }}
            sx={{ maxWidth: 420 }}
            inputProps={{ autoComplete: "off", spellCheck: false }}
          />
          <Button
            variant="contained" size="small"
            disabled={!valor.trim() || gravar.isPending}
            onClick={() => gravar.mutate()}
          >
            {gravar.isPending ? "Verificando…" : "Salvar"}
          </Button>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            {ONDE_PEGAR[linha.provedor]}
          </Typography>
        </Stack>
      )}

      {erro && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }} onClose={() => setErro("")}>
          {erro}
        </Alert>
      )}
    </Paper>
  );
}

export function ChavesDeApi({ chaves }: { chaves: Chaves }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        <KeyRoundedIcon sx={{ fontSize: 16, verticalAlign: "-3px", mr: 0.5 }} />
        Suas chaves de API
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 860, lineHeight: 1.6 }}>
        A Sophia usa a conta da sua empresa junto a cada fornecedor de IA. O consumo
        aparece na fatura de vocês, e nós não temos como ver o valor da chave depois
        de guardada — ela é cifrada e só o serviço consegue abrir.
      </Typography>

      {!chaves.tem_alguma && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <strong>A Sophia ainda não consegue responder.</strong> Cadastre pelo menos
          uma chave abaixo — sem credencial não há modelo para ela usar.
        </Alert>
      )}

      <Stack spacing={1.25}>
        {chaves.provedores.map((l) => (
          <UmaChave key={l.provedor} linha={l} />
        ))}
      </Stack>
    </Box>
  );
}
